import { useFlightPlansManagementContext } from '@components/cockpit/panels/flightPlan/FlightPlansManagementContext';
import {
  flightPlanFillLayer,
  flightPlanOutlineLayer,
  intersectionFillLayer,
  intersectionOutlineLayer,
} from '@components/map/layers/flightPlans/configuration/flightPlan.layer';
import { toFlightPlanFeatures } from '@components/map/layers/flightPlans/configuration/flightPlan.mapper';
import { usePrevious } from '@hooks/usePrevious';
import { bbox, booleanIntersects, featureCollection, intersect, multiPolygon } from '@turf/turf';
import { Feature, FeatureCollection } from 'geojson';
import { useEffect } from 'react';
import { Layer, LngLatBoundsLike, Source, useMap } from 'react-map-gl/mapbox';

import { SourceNameEnum } from '@/types/map.types';
import { ActivationAndFlightPlan } from '@/types/utm/flightPlan.types';

function computeIntersections(
  selectedActivationAndFlightPlan: ActivationAndFlightPlan | undefined,
  conflicts: ActivationAndFlightPlan[] | undefined,
) {
  if (
    !selectedActivationAndFlightPlan ||
    !conflicts?.length ||
    conflicts.some(
      (activationAndFlightPlan) =>
        activationAndFlightPlan.activation.id === selectedActivationAndFlightPlan.activation.id,
    )
  )
    return [];

  const selectedFeature = multiPolygon(selectedActivationAndFlightPlan.flightPlan.polygons.coordinates);
  const intersections: Feature[] = [];

  for (const conflict of conflicts) {
    const conflictFeature = multiPolygon(conflict.flightPlan.polygons.coordinates, {
      activationId: conflict.activation.id,
    });

    if (booleanIntersects(selectedFeature, conflictFeature)) {
      const result = intersect(featureCollection([selectedFeature, conflictFeature]));

      if (result) {
        intersections.push(result);
      }
    }
  }

  return intersections;
}

interface FlightPlanProps {
  selectedActivationAndFlightPlan: ActivationAndFlightPlan | undefined;
  conflictingActivationAndFlightPlans?: ActivationAndFlightPlan[];
}

function FlightPlanLayer({
  selectedActivationAndFlightPlan,
  conflictingActivationAndFlightPlans,
}: Readonly<FlightPlanProps>) {
  const { current: mapboxMap } = useMap();
  const { activationAndFlightPlans } = useFlightPlansManagementContext();
  const previousSelectedFlightPlan = usePrevious(selectedActivationAndFlightPlan);

  useEffect(() => {
    const map = mapboxMap?.getMap();
    if (!map?.getSource(SourceNameEnum.FLIGHT_PLANS)) return;

    if (!!previousSelectedFlightPlan && previousSelectedFlightPlan !== selectedActivationAndFlightPlan) {
      map.setFeatureState(
        { source: SourceNameEnum.FLIGHT_PLANS, id: `${previousSelectedFlightPlan.activation.id}` },
        { selected: false },
      );
    }

    if (!selectedActivationAndFlightPlan) return;

    const bounds = bbox(selectedActivationAndFlightPlan.flightPlan.polygons);
    map.fitBounds(bounds as LngLatBoundsLike, { padding: 50 });
    map.setFeatureState(
      {
        source: SourceNameEnum.FLIGHT_PLANS,
        id: `${selectedActivationAndFlightPlan.activation.id}`,
      },
      { selected: true },
    );
  }, [mapboxMap, activationAndFlightPlans, selectedActivationAndFlightPlan, previousSelectedFlightPlan]);

  const flightPlanFeatures: FeatureCollection = {
    type: 'FeatureCollection',
    features: toFlightPlanFeatures(activationAndFlightPlans),
  };

  const intersectionFeatures: FeatureCollection = {
    type: 'FeatureCollection',
    features: conflictingActivationAndFlightPlans
      ? computeIntersections(selectedActivationAndFlightPlan, conflictingActivationAndFlightPlans)
      : [],
  };

  return (
    <>
      <Source id={SourceNameEnum.FLIGHT_PLANS} type="geojson" data={flightPlanFeatures} promoteId="activationId">
        <Layer {...flightPlanFillLayer} />
        <Layer {...flightPlanOutlineLayer} />
      </Source>
      <Source id={SourceNameEnum.FLIGHT_PLAN_INTERSECTIONS} type="geojson" data={intersectionFeatures}>
        <Layer {...intersectionFillLayer} />
        <Layer {...intersectionOutlineLayer} />
      </Source>
    </>
  );
}

export default FlightPlanLayer;
